import React from 'react';
import { MdSensors, MdEdit, MdDownload } from 'react-icons/md';
import { format } from 'date-fns';
import { useLanguage } from '../../context/LanguageContext';

// Presentational panel: renders the group's full field set (company sensors — always
// auto-read — plus the admin-defined manual fields) for either the Spektrofotometre or
// Deney side. Field management (add/rename/delete) lives one level up, shared between
// both sides, since the two panels always show the exact same field set by construction.
// onExport is optional — only the training tab's panels get their own export button;
// the calculate tab reuses this component without one.
export default function YsaFieldListPanel({ title, icon, accent, fields, latestValues, values, onValueChange, onExport, exporting }) {
    const { t } = useLanguage();

    return (
        <div className="card" style={{ padding: 20, flex: 1, minWidth: 280 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {icon && <span style={{ color: accent || 'var(--primary)', fontSize: 18, display: 'flex' }}>{icon}</span>}
                    <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>{title}</span>
                </div>
                {onExport && (
                    <button
                        className="btn btn-secondary btn-sm"
                        onClick={onExport}
                        disabled={exporting}
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                    >
                        {exporting ? <span className="spinner spinner-sm" /> : <MdDownload size={14} />} {t('ysa.exportExcel')}
                    </button>
                )}
            </div>

            {fields.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '18px 0', color: 'var(--text-muted)', fontSize: 13 }}>
                    {t('ysa.noFields')}
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {fields.map(field => {
                        if (field.type === 'sensor') {
                            const latest = latestValues?.[field.sensorUUID];
                            const hasValue = latest && latest.lastValue !== '--' && latest.lastValue !== undefined;
                            return (
                                <div key={`sensor-${field._id}`} style={{
                                    display: 'flex', alignItems: 'center', gap: 10,
                                    padding: '10px 14px', borderRadius: 10,
                                    background: 'rgba(79,142,247,0.05)', border: '1px solid rgba(79,142,247,0.18)'
                                }}>
                                    <MdSensors size={16} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{field.name}</div>
                                        {hasValue && latest?.lastUpdated && (
                                            <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                                                {format(new Date(latest.lastUpdated), 'yyyy/MM/dd HH:mm:ss')}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{
                                        fontWeight: 700, fontFamily: 'var(--font-mono)', fontSize: 16, color: 'var(--primary)',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {hasValue ? `${latest.lastValue}${field.unit ? ` ${field.unit}` : ''}` : '—'}
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <div key={`manual-${field._id}`} style={{
                                display: 'flex', alignItems: 'center', gap: 10,
                                padding: '10px 14px', borderRadius: 10,
                                background: 'var(--bg-body)', border: '1px solid var(--border)'
                            }}>
                                <MdEdit size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                                <div style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>
                                    {field.name}
                                </div>
                                <input
                                    className="form-input form-input-sm"
                                    type="number"
                                    step="any"
                                    placeholder={t('ysa.manualValuePlaceholder')}
                                    value={values[field._id] ?? ''}
                                    onChange={e => onValueChange(field._id, e.target.value)}
                                    style={{ width: 130, textAlign: 'right' }}
                                />
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
